import api from 'services/api';
import { isFileEventType } from 'utils/eventTypes';

// POST /events/:zone_name/:name/raise_process_event
// POST /events/:zone_name/:name/raise_file_event
//
// One raise, dispatched on event type. Both endpoints address the event by
// zone and name rather than by id, so every segment is encodeURIComponent'd:
// an event name may legitimately contain a '/', which unescaped would 404 in
// the router before the handler ever runs.
//
// The response is discarded. A raise is a command, and every caller re-reads
// the affected surface afterwards rather than patching state from the receipt.

/** The event to raise, in the terms every caller already has to hand. */
export interface RaiseTarget {
  readonly zoneName: string;
  readonly eventName: string;
  readonly eventType: string | null;
}

/** Whether this event maps to a known raise endpoint. False for an unresolved flow-child path (null type) and anything outside the file/process buckets. */
export const isRaiseable = (target: RaiseTarget): boolean =>
  target.eventType !== null && (isFileEventType(target.eventType) || target.eventType === 'processEvent');

/**
 * Raise an event, keyed on zone + name.
 *
 * NOTE: the raise endpoints are NOT idempotent — each call inserts a fresh
 * raised `event_instances` row (no upsert). Callers must gate on the current
 * raised state and dedupe before firing, or the same event accrues multiple
 * outstanding occurrences.
 *
 * Throws if the type has no raise endpoint, or on HTTP error (404 for an
 * unknown zone/event, 403 without `events.update`).
 */
export const raiseEvent = async (target: RaiseTarget): Promise<void> => {
  if (!isRaiseable(target) || target.eventType === null) {
    throw new Error(`Cannot raise event of type ${target.eventType ?? 'unknown'}.`);
  }
  const zone = encodeURIComponent(target.zoneName);
  const name = encodeURIComponent(target.eventName);
  const endpoint = isFileEventType(target.eventType) ? 'raise_file_event' : 'raise_process_event';
  await api.post(`/events/${zone}/${name}/${endpoint}`);
};
